import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { API_BASE } from '../../config'
import { SafeImage } from '../../components/ui/SafeImage'

export interface ClientItem {
  id: number
  name: string
  industry?: string
  description?: string
  logo?: string
  websiteUrl?: string
}

function takeCycled<T>(list: T[], start: number, count: number): T[] {
  if (!list.length) return []
  const out: T[] = []
  for (let i = 0; i < count; i++) out.push(list[(start + i) % list.length])
  return out
}

function ClientMarqueeRow({
  items,
  direction,
  className,
}: {
  items: ClientItem[]
  direction: 'ltr' | 'rtl'
  className?: string
}) {
  if (items.length === 0) return null
  const loop = [...items, ...items]
  return (
    <div className={`relative overflow-hidden ${className ?? ''}`}>
      <div
        className={`flex w-max items-center gap-12 py-2 sm:gap-16 md:gap-20 ${
          direction === 'ltr' ? 'animate-marquee-ltr' : 'animate-marquee-rtl'
        }`}
      >
        {loop.map((client, i) => (
          <div
            key={`${client.id}-${i}`}
            className="flex h-[100px] w-[140px] shrink-0 flex-col items-center justify-center rounded-2xl border border-slate-200/80 bg-white/90 px-4 shadow-sm dark:border-slate-700/80 dark:bg-slate-900/80 sm:h-[110px] sm:w-[160px]"
          >
            <SafeImage
              src={client.logo}
              alt=""
              label={client.name}
              tone="logo"
              className="flex h-12 w-full max-w-[140px] flex-1 items-center justify-center sm:h-14 sm:max-w-[150px]"
              imgClassName="h-full w-full object-contain grayscale transition-all duration-300 hover:grayscale-0"
              loading="lazy"
            />
            <span className="mt-1 max-w-full truncate text-center text-[10px] font-medium text-slate-500 dark:text-slate-400 sm:text-xs">
              {client.name}
            </span>
          </div>
        ))}
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-12 bg-gradient-to-r from-slate-50/50 to-transparent dark:from-slate-900/30 sm:w-20"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-12 bg-gradient-to-l from-slate-50/50 to-transparent dark:from-slate-900/30 sm:w-20"
        aria-hidden
      />
    </div>
  )
}

export function OurClients() {
  const [clients, setClients] = useState<ClientItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/clients`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.clients)) return
        setClients(data.clients)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const rowTop = takeCycled(clients, 0, 5)
  const rowBottom = takeCycled(clients, 5, 4)

  if (loading && clients.length === 0) return null
  if (clients.length === 0) return null

  return (
    <section className="section-pad relative overflow-hidden bg-slate-50/50 px-4 sm:px-6 lg:px-8 dark:bg-slate-900/30">
      <div className="container-wide relative">
        <motion.div
          className="mb-8 text-center sm:mb-12"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-sm font-semibold uppercase tracking-wider text-accent">Trusted by</span>
          <h2 className="section-heading mt-2 mb-3">Our Clients</h2>
          <p className="section-sub mx-auto max-w-xl">
            Businesses across industries trust DigiSutra for growth and digital presence.
          </p>
        </motion.div>

        <div className="space-y-4 sm:space-y-5">
          <ClientMarqueeRow items={rowTop} direction="ltr" />
          <ClientMarqueeRow items={rowBottom} direction="rtl" />
        </div>

        <motion.div
          className="mt-10 text-center sm:mt-12"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Link to="/clients" className="btn-secondary group inline-flex items-center">
            View all clients
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
